<template>
    <div class="modal fade show hotel-modal" aria-labelledby="billAdjustReceiptModalTitle">
        <div class="modal-dialog modal-dialog-centered modal-lg">
            <div class="modal-content">
                <div class="d-flex justify-content-between align-items-center gap-1 px-1 my-1">
                    <h1 class="modal-heading mb-0">Money Receipt - {{ row.room_name }}</h1>
                    <div class="d-flex align-items-center gap-1 flex-shrink-0">
                        <button @click="onClose" type="button" class="btn-close" aria-label="Close"></button>
                    </div>
                </div>

                <hr class="mt-0">

                <form @submit.prevent="onSubmit">
                    <div class="modal-body">
                        <div class="row g-1 mb-2">
                            <div class="col-4">
                                <div class="summary-card summary-total">
                                    <span class="summary-label">Bill Amount</span>
                                    <span class="summary-value">{{ commaFormat(billAmount) }}</span>
                                </div>
                            </div>
                            <div class="col-4">
                                <div class="summary-card summary-paid">
                                    <span class="summary-label">Receipt</span>
                                    <span class="summary-value">{{ commaFormat(receiptAmount) }}</span>
                                </div>
                            </div>
                            <div class="col-4">
                                <div class="summary-card summary-due">
                                    <span class="summary-label">Bill Due</span>
                                    <span class="summary-value">{{ commaFormat(billDue) }}</span>
                                </div>
                            </div>
                        </div>
                        <div class="detail-section">
                            <div class="row g-1">
                                <div class="col-6 mb-1">
                                    <label class="form-label">Received In</label>
                                    <v-select
                                        placeholder="Select Cash & Bank Account"
                                        v-model="formData.account_head_id"
                                        :options="accountHeads"
                                        :reduce="head => head.id"
                                        label="name"
                                    />
                                </div>
                                <div class="col-6 mb-1">
                                    <label class="form-label">Received Date</label>
                                    <input v-model="formData.date" type="date" class="form-control"/>
                                </div>
                                <div class="col-6 mb-1">
                                    <label class="form-label">Amount</label>
                                    <input v-model="formData.amount" type="number" min="0" :max="billAmount" class="form-control"/>
                                </div>
                                <div class="col-6 mb-1">
                                    <label class="form-label">Reference</label>
                                    <input v-model="formData.reference" type="text" class="form-control"/>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="d-flex flex-wrap justify-content-end gap-1 mt-2 px-2 pb-2">
                        <button type="button" class="btn btn-outline-secondary flex-grow-1 flex-sm-grow-0" @click="onClose">Cancel</button>
                        <button type="submit" :disabled="loading" class="btn btn-primary flex-grow-1 flex-sm-grow-0">Save Bill & Receipt</button>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script setup>
import {computed, inject, onMounted, ref} from 'vue';
import figureFormatter from '@/services/utils/figureFormatter';
import handleReceipt from '@/services/modules/receipt';
import {useRoute} from "vue-router";

const props = defineProps({
    row: {
        type: Object,
        required: true,
    },
    loading: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['onClose', 'onSubmit']);

const showError = inject('showError');
const { commaFormat } = figureFormatter();

const billAmount = computed(() => Number(props.row.bill_amount || props.row.adjustment_amount || 0));
const { fetchCashAndBank } = handleReceipt();
const $route = useRoute();

const formData = ref({
    account_head_id: null,
    date: new Date().toISOString().split('T')[0],
    amount: billAmount.value,
    reference: '',
});
const accountHeads = ref([]);

const receiptAmount = computed(() => Number(formData.value.amount || 0));

const billDue = computed(() => billAmount.value - receiptAmount.value);

const validate = () => {
    if (!formData.value.account_head_id) {
        showError('Please select the received account');
        return false;
    }
    if (!formData.value.amount || Number(formData.value.amount) <= 0) {
        showError('Please enter a valid amount');
        return false;
    }
    if (Number(formData.value.amount) > billAmount.value) {
        showError('Amount cannot exceed bill amount');
        return false;
    }
    return true;
};

const onClose = () => {
    emit('onClose');
};

const selectDefaultAccountHead = () => {
    if (formData.value.account_head_id) return;
    if (!accountHeads.value.length) return;
    formData.value.account_head_id = accountHeads.value[0].id;
};

onMounted(() => {
    fetchCashAndBank($route.params.companyId).then(res => {
        if (!res.data) return;
        accountHeads.value = res.data;
        selectDefaultAccountHead();
    });
});

const onSubmit = () => {
    if (props.loading) return;
    if (!validate()) return;
    emit('onSubmit', Object.assign({}, formData.value));
};
</script>

<style scoped>
.hotel-modal {
    display: block;
    background: #1c1d1c33;
}
.modal-heading {
    font-size: 1.5rem;
}
.detail-section {
    background: #f8f8f8;
    border: 1px solid #ebe9f1;
    border-radius: 0.5rem;
    padding: 0.75rem;
}
.summary-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 0.65rem 0.5rem;
    border-radius: 0.5rem;
    border: 1px solid transparent;
    height: 100%;
}
.summary-label {
    font-size: 0.72rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    opacity: 0.75;
    margin-bottom: 0.15rem;
}
.summary-value {
    font-size: 1.05rem;
    font-weight: 700;
    line-height: 1.2;
}
.summary-total {
    background: #eef2f7;
    border-color: #dfe6ef;
    color: #384250;
}
.summary-paid {
    background: #e7f6ec;
    border-color: #cdeed8;
    color: #1a7f43;
}
.summary-due {
    background: #fdeceb;
    border-color: #f8d5d2;
    color: #c0392b;
}
</style>
